/** HTTP clients: gateway chính vs prediction/simulation. */
import axios, { type AxiosInstance } from 'axios';

import { TOKEN_KEY, REFRESH_TOKEN_KEY, API_TIMEOUT } from '@/config/constants';
import { env } from '@/config/env';
import { getOrCreateDeviceId, setDeviceId } from '@/utils/deviceId';
import { parseLoginEnvelope } from '@/utils/parseAuthEnvelope';
import { storage } from '@/utils/storage';

function stripTrailingSlash(url: string): string {
  return url.replace(/\/$/, '');
}

/** Mọi instance axios dùng Bearer + refresh — cập nhật `defaults` sau refresh. */
const authClients: AxiosInstance[] = [];

/**
 * Gateway chính (auth, ops, Swagger thường ở đây — ví dụ `https://ies-gateway.dev.3si.vn`).
 * @see env.API_BASE_URL
 */
export const apiClient = createAuthenticatedApiClient(env.API_BASE_URL);

/**
 * Dịch vụ prediction/simulation khi tách host (`VITE_PREDICTION_SIMULATION_API_BASE_URL`, ví dụ :30070).
 * Nếu không set biến đó → dùng cùng {@link apiClient}.
 */
export const predictionSimulationApiClient =
  env.PREDICTION_SIMULATION_API_BASE_URL.trim() !== ''
    ? createAuthenticatedApiClient(
        stripTrailingSlash(env.PREDICTION_SIMULATION_API_BASE_URL.trim()),
      )
    : apiClient;

function createAuthenticatedApiClient(baseURL: string): AxiosInstance {
  const client = axios.create({
    baseURL,
    timeout: API_TIMEOUT,
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
  });

  authClients.push(client);

  client.interceptors.request.use((config) => {
    const token = storage.get<string>(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['X-Device-Id'] = getOrCreateDeviceId();
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (!originalRequest) return Promise.reject(error);

      const reqUrl = String(originalRequest.url ?? '');
      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        !reqUrl.includes('/api/auth/login') &&
        !reqUrl.includes('/api/auth/logout') &&
        !reqUrl.includes('/api/auth/refresh')
      ) {
        if (isRefreshing) {
          return new Promise<string>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return client(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const { data } = await axios.post(`${env.API_BASE_URL}/api/auth/refresh`, undefined, {
            withCredentials: true,
            headers: { 'X-Device-Id': getOrCreateDeviceId() },
          });
          const parsed = parseLoginEnvelope(data);
          if (!parsed) throw new Error('Refresh failed');

          storage.set(TOKEN_KEY, parsed.access_token);
          storage.remove(REFRESH_TOKEN_KEY);
          setDeviceId(parsed.device_id);

          for (const c of authClients) {
            c.defaults.headers.common.Authorization = `Bearer ${parsed.access_token}`;
          }
          originalRequest.headers.Authorization = `Bearer ${parsed.access_token}`;

          processQueue(null, parsed.access_token);
          isRefreshing = false;

          return client(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError as Error, null);
          isRefreshing = false;

          storage.remove(TOKEN_KEY);
          storage.remove(REFRESH_TOKEN_KEY);
          window.location.href = '/login';

          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    },
  );

  return client;
}

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token as string);
    }
  });

  failedQueue = [];
};
